import React, { ReactNode } from 'react';
import { Box } from '@cp/ds/src/components/box';
import { Card } from '@cp/ds/src/components/card';
import { CardProps } from '@cp/ds/src/components/card/model';
import { Stack } from '@cp/ds/src/components/stack';
import { styled } from '@cp/ds/src/theme/styled';
import { CatalogItemViewModel } from '@common/model/catalog/item/view';
import { Typography } from '@cp/ds/src/components/typography';
import { useIsMobile } from '@cp/ds/src/hooks';
import { MicrophoneIcon } from '@cp/ds/src/icons/microphone';
import { ResumeFeatureRender } from './resumeFeatureRender';

const Root = styled(Card)({
  borderRadius: '16px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  '&:hover': {
    backgroundColor: 'white',
  },
});

const StyledPhoto = styled(Box)({
  position: 'relative',
  width: '100%',
  zIndex: 0,
  '&:after': {
    position: 'absolute',
    content: '""',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    zIndex: 1,
    marginBottom: '-1px',
    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 71.48%, #FFFFFF 100%)',
  },
});

const StyledHeader = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '-11px',
  left: 0,
  zIndex: 3,
  paddingInline: '8px',
  [theme.breakpoints.up('md')]: {
    paddingInline: '12px',
  },
}));

const TopInfoBlock = styled(Stack)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  padding: '6px',
  [theme.breakpoints.up('md')]: {
    padding: '12px',
  },
}));

export interface ResumeNewCardProps extends CardProps {
  hidden?: ReactNode | ReactNode[];
  header?: ReactNode;
  achievements?: ReactNode | ReactNode[];
  profilePhoto?: ReactNode;
  mediaButton?: boolean;
  footer?: ReactNode;
  showDescription?: boolean;
  data?: CatalogItemViewModel;
  itemPrice?: ReactNode;
}

const MicrophoneWrapper = styled(Stack)(({ theme }) => ({
  position: 'absolute',
  left: '6px',
  top: '34px',
  zIndex: 2,
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: theme.palette['brand pink'],
  border: `1px solid ${theme.palette.white}`,
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  [theme.breakpoints.up('md')]: {
    left: '12px',
    top: '40px',
  },
}));

export const ResumeCardNew: React.FC<ResumeNewCardProps> = ({
  mediaButton,
  itemPrice,
  achievements,
  profilePhoto,
  data,
  header,
  ...props
}) => {
  const isMobile = useIsMobile();

  return (
    <Root {...props}>
      <StyledPhoto>
        {profilePhoto}
        <TopInfoBlock alignItems="flex-start" direction="row" justifyContent="space-between">
          {itemPrice}
          <Stack alignItems="flex-end" gap={0.5}>
            <ResumeFeatureRender data={data} featurePath="sidebar.right" />
            {achievements}
          </Stack>
        </TopInfoBlock>
            { mediaButton &&
        <MicrophoneWrapper>
           <MicrophoneIcon sx={{ fontSize: '16px', color: 'white' }} />
        </MicrophoneWrapper>
        }
        <StyledHeader>{header}</StyledHeader>
      </StyledPhoto>
      <Typography
        component="p"
        numberOfLines={isMobile ? 5 : 4}
        sx={{
          px: { xs: '8px', md: '12px' },
          typography: { xs: 'small text', md: 'caption' },
          mt: '15px',
          mb: { xs: '8px', md: '12px' },
        }}
      >
        {data?.translation?.description}
      </Typography>
    </Root>
  );
};
